import type { BackendTypes } from '@tf/api';
import {
	Alert,
	Button,
	createModal,
	createStyles,
	DateInput,
	Flex,
	Icon,
	Input,
	Select,
	TFNotifier,
	TFText,
	useDisclosure,
	useModalControl,
} from '@tf/ui';
import { C, D, fmt } from '@tf/utils';
import type React from 'react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import type { FileInfo } from '../../../../../../api/files';
import { useConfigContext } from '../../../../../../hooks';
import { isImageFile } from '../../utils';

import { getSegmentsData } from './getSegmentsData';

const useStyles = createStyles(({ colors }) => ({
	checkButton: {
		fontWeight: 500,
		fontSize: 13,
		border: `1px solid transparent`,
		backgroundColor: colors.gray[1],
		'&:disabled': {
			color: colors.gray[5],
			backgroundColor: colors.gray[1],
		},
	},
	select: {
		margin: '.75rem .75rem .25rem .75rem',
	},
	footer: {
		marginTop: '1rem',
		padding: '.75rem .75rem .75rem',
		border: `1px solid ${colors.gray[2]}`,
	},
}));

// no document below this size has ever passed the iDenfy check, see TF-3169
const LOW_RESOLUTION_SIZE = 60 * 1024;

const MAX_REQUEST_BASE64_SIZE = 20_000_000;

const MAX_REQUEST_FILES_SIZE_MB = Math.floor(((MAX_REQUEST_BASE64_SIZE / 4) * 3) / (1024 * 1024));

const toBase64Size = (size: number) => 4 * Math.ceil(size / 3);

function notEmpty<T>(value: T): NonNullable<T> {
	if (!value) {
		throw new Error('value is empty');
	}
	return value;
}

type ParamsForm = {
	backSideDocument: string | null;
	frontSideDocument: string | null;
	residencyCountry: string | null;
	documentType: BackendTypes.DocumentCheckKind | null;
	firstName: string;
	lastName: string;
	dateOfBirth: string | null;
};

const EditParamsModal = createModal<{
	files: FileInfo[];
	onCheckDocument: (checkParams: BackendTypes.DocumentCheckParams) => void;
}>(({ files, onCheckDocument }) => {
	const { classes } = useStyles();
	const { onClose } = useModalControl();

	const getEnumOptions = useConfigContext((s) => s.getEnumOptions);
	const countries = getEnumOptions('common__enum__country') || [];
	const documentsKinds = getEnumOptions('DocumentCheckKind') || [];
	const selectFilesOptions = files.filter(isImageFile).map((file) => ({
		label: file.meta['@filename'],
		value: file.id,
	}));

	const formContext = useFormContext();

	const [params, setParams] = useState<ParamsForm>({
		backSideDocument: null,
		frontSideDocument: null,
		residencyCountry: null,
		documentType: null,
		firstName: '',
		lastName: '',
		dateOfBirth: null,
		...getSegmentsData(formContext.getValues()),
	});

	const updateParamsField = (key: keyof ParamsForm, value: string) => {
		setParams((prev) => ({ ...prev, [key]: value }));
	};

	const getCheckParams = (): BackendTypes.DocumentCheckParams | null => {
		try {
			const frontSideFile = files.find((file) => file.id === params.frontSideDocument);

			const checkParams: BackendTypes.DocumentCheckParams = {
				// dummy status to check idenfy jobs requests in dev mode
				// more info here https://documentation.idenfy.com/integration/DummySession#sending-request
				// extraParams: {
				// 	status: 'APPROVED',
				// 	manualFaceMatchResult: 'FACE_MATCH',
				// 	manualDocumentValidity: 'DOC_VALIDATED',
				// },
				firstName: notEmpty(params.firstName),
				lastName: notEmpty(params.lastName),
				dateOfBirth: notEmpty(params.dateOfBirth),
				country: notEmpty(params.residencyCountry),
				documentKind: notEmpty(params.documentType),
				serviceKind: 'idenfy',
				frontSide: {
					filename: notEmpty(frontSideFile).meta['@filename'],
					storageKey: notEmpty(frontSideFile).id,
				},
			};

			if (params.documentType === 'ID_CARD') {
				const backSideFile = files.find((file) => file.id === params.backSideDocument);

				checkParams.backSide = {
					filename: notEmpty(backSideFile).meta['@filename'],
					storageKey: notEmpty(backSideFile).id,
				};
			}

			return checkParams;
		} catch (_e) {
			return null;
		}
	};

	const requestFileIds = [params.frontSideDocument, params.documentType === 'ID_CARD' ? params.backSideDocument : null];
	const requestFiles = files.filter((file) => requestFileIds.includes(file.id));
	const requestBase64Size = requestFiles.reduce((total, file) => total + toBase64Size(file.meta['@size']), 0);
	const isRequestTooLarge = requestBase64Size > MAX_REQUEST_BASE64_SIZE;

	const isSubmitButtonDisabled = getCheckParams() === null || isRequestTooLarge;

	const lowResolutionFiles = files.filter(
		(file) =>
			[params.frontSideDocument, params.backSideDocument].includes(file.id) && file.meta['@size'] < LOW_RESOLUTION_SIZE,
	);

	const checkDocument = () => {
		const checkParams = getCheckParams();
		if (!checkParams) {
			TFNotifier.error('Please fill all required fields');
			return;
		}
		onCheckDocument(checkParams);
		onClose();
	};

	return (
		<>
			<Input.Wrapper required className={classes.select} label="First name">
				<Input value={params.firstName} onChange={({ target }) => updateParamsField('firstName', target.value)} />
			</Input.Wrapper>
			<Input.Wrapper required className={classes.select} label="Last name">
				<Input value={params.lastName} onChange={({ target }) => updateParamsField('lastName', target.value)} />
			</Input.Wrapper>
			<DateInput
				required
				placeholder="Select date..."
				className={classes.select}
				label="Date of birth"
				value={params.dateOfBirth ? D.jsDateFromString(params.dateOfBirth) : null}
				onChange={(value) => {
					if (value) {
						updateParamsField('dateOfBirth', fmt.toDate(value, { preset: 'date_struct' }));
					}
				}}
			/>
			<Select
				required
				className={classes.select}
				label="Document type"
				value={params.documentType}
				onChange={(value) => {
					if (value) {
						updateParamsField('documentType', value);
					}
				}}
				data={documentsKinds}
			/>
			<Select
				required
				className={classes.select}
				value={params.residencyCountry}
				onChange={(value) => {
					if (value) {
						updateParamsField('residencyCountry', value);
					}
				}}
				searchable
				label="Country of issue"
				data={C.groupCountries(countries)}
			/>
			<Select
				required
				className={classes.select}
				value={params.frontSideDocument}
				onChange={(value) => {
					if (value) {
						updateParamsField('frontSideDocument', value);
					}
				}}
				searchable
				label="Document front side"
				data={selectFilesOptions.filter((file) => file.value !== params.backSideDocument)}
			/>
			<Select
				required={params.documentType === 'ID_CARD'}
				className={classes.select}
				value={params.backSideDocument}
				onChange={(value) => {
					if (value) {
						updateParamsField('backSideDocument', value);
					}
				}}
				searchable
				label="Document back side"
				data={selectFilesOptions.filter((file) => file.value !== params.frontSideDocument)}
			/>
			{isRequestTooLarge ? (
				<Alert color="red" className={classes.select} icon={<Icon.IconAlertCircle size={18} stroke={1.8} />}>
					<TFText size="sm">
						Too large for verification:{' '}
						{requestFiles.map((file) => `${file.meta['@filename']} (${fmt.toFileSize(file.meta['@size'])})`).join(', ')}
						. The verification provider accepts at most {MAX_REQUEST_FILES_SIZE_MB} MB of files per check. Please use a
						smaller scan, for example a JPG.
					</TFText>
				</Alert>
			) : null}
			{lowResolutionFiles.length > 0 ? (
				<Alert color="yellow" className={classes.select} icon={<Icon.IconAlertCircle size={18} stroke={1.8} />}>
					<TFText size="sm">
						Low resolution:{' '}
						{lowResolutionFiles
							.map((file) => `${file.meta['@filename']} (${fmt.toFileSize(file.meta['@size'])})`)
							.join(', ')}
						. The verification provider usually cannot recognise a document in such a file. You can still run the check,
						but a higher quality scan or a photo works better.
					</TFText>
				</Alert>
			) : null}
			<Flex className={classes.footer}>
				<Button mr="0.75rem" fullWidth variant="light" onClick={onClose}>
					Cancel
				</Button>
				<Button fullWidth disabled={isSubmitButtonDisabled} onClick={checkDocument}>
					Check
				</Button>
			</Flex>
		</>
	);
});

interface Props {
	files: FileInfo[];
	hasPreviousCheck: boolean;
	onCheckDocument: (checkParams: BackendTypes.DocumentCheckParams) => void;
}

export const StartCheckButton: React.FC<Props> = ({ onCheckDocument, hasPreviousCheck, files }) => {
	const { classes } = useStyles();

	const editParamsModal = useDisclosure();

	return (
		<>
			<Button
				size="sm"
				variant="light"
				className={classes.checkButton}
				leftSection={<Icon.IconCheck size={16} stroke={1.8} />}
				onClick={editParamsModal.open}
				disabled={!files?.length}
			>
				{hasPreviousCheck ? 'Check new document' : 'Check the document'}
			</Button>
			<EditParamsModal
				title="Choose the document"
				opened={editParamsModal.isOpen}
				contentProps={{ files, onCheckDocument }}
				onClose={editParamsModal.close}
			/>
		</>
	);
};
